import { trace } from "@opentelemetry/api";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { defaultResource, resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";

interface OtelConfig {
  enabled: boolean;
  endpoint: string;
  serviceName: string;
  environment: string;
  backendUrl: string;
  samplingRate?: number;
  debugMode?: boolean;
}

export function initializeOTel(config: OtelConfig) {
  // Early return if telemetry is disabled
  if (!config.enabled) {
    return;
  }

  const resource = defaultResource().merge(
    resourceFromAttributes({
      [ATTR_SERVICE_NAME]: config.serviceName,
      [ATTR_SERVICE_VERSION]: "1.0.0",
      "deployment.environment": config.environment,
    }),
  );

  const exporter = new OTLPTraceExporter({
    url: `${config.endpoint}/v1/traces`,
    headers: {},
  });

  const batchProcessor = new BatchSpanProcessor(exporter, {
    maxQueueSize: 100,
    maxExportBatchSize: 10,
    scheduledDelayMillis: 500,
    exportTimeoutMillis: 30000,
  });

  // Configure sampling rate (default to 1.0 for full sampling)
  const samplingRate = config.samplingRate ?? 1.0;
  const sampler = new ParentBasedSampler({
    root: new TraceIdRatioBasedSampler(samplingRate),
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [batchProcessor],
    sampler,
  });

  provider.register({
    propagator: new W3CTraceContextPropagator(),
  });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [new RegExp(config.backendUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))],
        clearTimingResources: true,
        applyCustomAttributesOnSpan: (span, _request, response) => {
          if (response && "headers" in response) {
            span.setAttribute("http.response.content_type", response.headers.get("content-type") || "");
          }
        },
      }),

      new DocumentLoadInstrumentation(),

      new UserInteractionInstrumentation({
        eventNames: ["click", "submit"],
        shouldPreventSpanCreation: (_eventType, element) => {
          return element.hasAttribute("data-no-trace");
        },
      }),
    ],
  });

  bindBareGlobalListeners();
}

/**
 * UserInteractionInstrumentation patches EventTarget.prototype.addEventListener and keys a
 * WeakMap by the call's `this`. Bare global calls such as web-vitals' `addEventListener(...)`
 * pass `this === undefined`, so the patch throws and aborts app startup. Route bare calls on
 * window through the patched functions with `this` bound to window.
 */
export function bindBareGlobalListeners() {
  for (const name of ["addEventListener", "removeEventListener"] as const) {
    const patched = window[name];
    window[name] = function (this: unknown, ...args: Parameters<Window[typeof name]>) {
      return patched.apply(this ?? window, args);
    } as Window[typeof name];
  }
}

export function createSpan(name: string, attributes?: Record<string, string | number | boolean>) {
  const tracer = trace.getTracer("customer-portal-ui");
  const span = tracer.startSpan(name);

  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, value);
    });
  }

  return {
    end: () => span.end(),
    addEvent: (eventName: string, eventAttributes?: Record<string, string | number | boolean>) => {
      span.addEvent(eventName, eventAttributes);
    },
  };
}
